import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Building2, Users, UserCheck, Factory, FileText, MessageSquare,
  Workflow, MailWarning, BarChart3, ScrollText, Settings, LogOut, Bell, CheckCheck, CreditCard, ClipboardList, LifeBuoy, Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { timeAgo } from "@/lib/format";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/organization", label: "Organizations", icon: Building2 },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/buyers", label: "Buyers", icon: UserCheck },
  { to: "/admin/suppliers", label: "Suppliers", icon: Factory },
  { to: "/admin/pos", label: "Purchase Orders", icon: FileText },
  { to: "/admin/communications", label: "Communications", icon: MessageSquare },
  { to: "/admin/automation", label: "Automation Rules", icon: Workflow },
  { to: "/admin/templates", label: "Message Templates", icon: MailWarning },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { to: "/admin/demo-requests", label: "Demo Requests", icon: ClipboardList },
  { to: "/admin/support", label: "Support Tickets", icon: LifeBuoy },
  { to: "/admin/sales-requests", label: "Sales Requests", icon: Handshake },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r bg-slate-950">
        <div className="flex h-14 items-center gap-2 border-b border-slate-800 px-4">
          <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto rounded bg-white px-1.5 py-0.5" />
          <span className="ml-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Admin</span>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2" data-testid="admin-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-white text-slate-900" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3 text-xs text-slate-500">
          <p className="font-medium text-slate-300">{user?.org_name}</p>
          <p className="mt-0.5">Organization Admin</p>
        </div>
      </aside>

      <div className="pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-4 border-b bg-white px-6">
          <GlobalSearch linkBase="/admin/pos" placeholder="Search all POs, suppliers, items, tracking…" />
          <div className="ml-auto flex items-center gap-2">
            <DropdownMenu onOpenChange={(o) => o && loadNotifs()}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="relative" data-testid="notifications-bell">
                  <Bell className="h-4 w-4" />
                  {notifs.unread > 0 && (
                    <span data-testid="notifications-unread-count" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                      {notifs.unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 bg-white">
                <DropdownMenuLabel className="flex items-center justify-between">
                  Notifications
                  <button data-testid="mark-all-read-btn" className="flex items-center gap-1 text-xs font-normal text-blue-700 hover:underline"
                    onClick={() => api.post("/notifications/read-all").then(loadNotifs)}>
                    <CheckCheck className="h-3 w-3" /> Mark all read
                  </button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="max-h-80 overflow-y-auto">
                  {notifs.notifications.length === 0 && <p className="p-4 text-sm text-muted-foreground">No notifications yet.</p>}
                  {notifs.notifications.slice(0, 15).map((n) => (
                    <DropdownMenuItem key={n.id} data-testid={`notification-${n.id.slice(0, 8)}`}
                      className={`flex cursor-pointer flex-col items-start gap-0.5 p-3 ${!n.read ? "bg-blue-50/60" : ""}`}
                      onClick={() => n.po_id && navigate(`/admin/pos/${n.po_id}`)}>
                      <span className="text-sm font-medium">{n.title}</span>
                      <span className="text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{timeAgo(n.created_at)}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  {user?.name?.[0]?.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuLabel>
                  <p className="text-sm">{user?.name}</p>
                  <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
